import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiSearch, FiX } from "react-icons/fi";
import "../assests/css/search.css";

const API = "https://admin.bridge365.in/wp-json/wc/v3";
const CK = "ck_1d0bfb52b9046a39fb2d906f89f691968931bce6";
const CS = "cs_956c51f2c9249f343bec7f0ecbfd7738654ba424";
const RECENT_KEY = "bridge365_recent_searches";

function readRecentSearches() {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(stored) ? stored.slice(0, 8) : [];
  } catch (error) {
    return [];
  }
}

function SearchBox({ forceOpen = false, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [recentSearches, setRecentSearches] = useState(readRecentSearches);
  const [recommended, setRecommended] = useState([]);
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    fetch(`${API}/products?per_page=4&orderby=popularity&consumer_key=${CK}&consumer_secret=${CS}`)
      .then((res) => res.json())
      .then((data) => setRecommended(Array.isArray(data) ? data : []))
      .catch(() => setRecommended([]));
  }, []);

  const closeSearch = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (!forceOpen) return;

    setIsOpen(true);
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [forceOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (event.target.closest(".mobile-search-btn")) return;
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        closeSearch();
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    closeSearch();
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const term = query.trim();
    if (!term) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    debounceRef.current = setTimeout(() => {
      fetch(
        `${API}/products?search=${encodeURIComponent(term)}&per_page=6&consumer_key=${CK}&consumer_secret=${CS}`
      )
        .then((res) => res.json())
        .then((data) => setResults(Array.isArray(data) ? data : []))
        .catch(() => setResults([]))
        .finally(() => setIsSearching(false));
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const saveRecentSearch = (term) => {
    const next = [term, ...recentSearches.filter((item) => item.toLowerCase() !== term.toLowerCase())].slice(0, 8);
    setRecentSearches(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  };

  const removeRecentSearch = (term) => {
    const next = recentSearches.filter((item) => item !== term);
    setRecentSearches(next);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  };

  const resetHistory = () => {
    setRecentSearches([]);
    localStorage.removeItem(RECENT_KEY);
  };

  const goToSearch = (term) => {
    const nextTerm = term.trim();
    if (!nextTerm) return;

    saveRecentSearch(nextTerm);
    setQuery(nextTerm);
    closeSearch();
    navigate(`/shop?q=${encodeURIComponent(nextTerm)}`);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    goToSearch(query);
  };

  const showResults = query.trim().length > 0;
  const listItems = showResults ? results : recommended;

  return (
    <div className={`header-search${isOpen ? " is-open" : ""}`} ref={boxRef}>
      <form className="header-search-form" onSubmit={handleSubmit}>
        <fieldset>
          <legend>Search for the Items</legend>
          <input
            ref={inputRef}
            type="search"
            value={query}
            placeholder=""
            autoComplete="off"
            onFocus={() => setIsOpen(true)}
            onChange={(event) => {
              setQuery(event.target.value);
              setIsOpen(true);
            }}
          />
          <button type="submit" className="header-search-icon" aria-label="Search">
            <FiSearch />
          </button>
        </fieldset>
      </form>

      {isOpen && (
        <div className="header-search-dropdown">
          {!showResults && recentSearches.length > 0 && (
            <div className="header-search-section">
              <div className="header-search-section-head">
                <h4>Recent Search</h4>
                <button type="button" onClick={resetHistory}>
                  Reset History
                </button>
              </div>
              <div className="header-search-chips">
                {recentSearches.map((term) => (
                  <button
                    key={term}
                    type="button"
                    className="header-search-chip"
                    onClick={() => goToSearch(term)}
                  >
                    <span>{term}</span>
                    <i
                      onClick={(event) => {
                        event.stopPropagation();
                        removeRecentSearch(term);
                      }}
                    >
                      <FiX />
                    </i>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="header-search-section">
            <div className="header-search-section-head">
              <h4>{showResults ? "Search Results" : "Recommended"}</h4>
            </div>

            {isSearching ? (
              <p className="header-search-empty">Searching...</p>
            ) : listItems.length === 0 ? (
              <p className="header-search-empty">
                {showResults ? "No products found." : "No recommended products."}
              </p>
            ) : (
              <ul className="header-search-results">
                {listItems.map((product) => (
                  <li key={product.id}>
                    <Link
                      to={`/product/${product.id}`}
                      onClick={() => {
                        if (query.trim()) {
                          saveRecentSearch(query.trim());
                        }
                        closeSearch();
                      }}
                    >
                      <img
                        src={product.images?.[0]?.src || "/no-image.png"}
                        alt={product.name}
                      />
                      <span>{product.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchBox;
